// src/components/Assets/models.js
// Data models and constants for Assets module

/**
 * Asset Model Template
 */
export const AssetModel = {
  id: null,
  tenantId: null,
  name: "",
  details: ""
};

/**
 * Asset Permission Model Template
 */
export const AssetPermissionModel = {
  id: null,
  tenantId: null,
  farmId: null,
  assetId: null,
  farm: null,           // Nested Farm object
  asset: null,          // Nested Asset object
  status: "pending",    // "pending" | "approved" | "denied"
  requestDate: null,    // Backend uses "requestDate" not "requestStartDate"
  requestEndDate: null,
  usageTime: null,
  notes: ""
};

/**
 * Asset Usage Model Template (tracks actual usage of assets)
 */
export const AssetUsageModel = {
  id: null,
  tenantId: null,
  farmId: null,
  assetId: null,
  permissionId: null,    // Reference to permission request
  actualStartDate: null,
  actualEndDate: null,
  actualUsageTime: null,  // Actual hours used
  notes: ""
};

/**
 * Asset Expense Model Template
 */
export const AssetExpenseModel = {
  id: null,
  tenantId: null,
  farmId: null,
  assetId: null,
  date: null,
  value: 0,
  type: "expense",
  category: "",
  details: ""
};

/**
 * Permission Status Enum
 * Includes localized labels and colors for UI rendering
 */
export const PermissionStatus = {
  PENDING: {
    value: "pending",
    labelEn: "Pending",
    labelAr: "تحت المراجعة",
    color: "#6c757d",
    bgColor: "#f8f9fa"
  },
  APPROVED: {
    value: "approved",
    labelEn: "Approved",
    labelAr: "تمت الموافقة",
    color: "#28a745",
    bgColor: "#d4edda"
  },
  DENIED: {
    value: "denied",
    labelEn: "Denied",
    labelAr: "مرفوض",
    color: "#dc3545",
    bgColor: "#f8d7da"
  }
};

/**
 * Helper function to get status configuration
 * @param {string} statusValue - Status value ("pending", "approved", "denied")
 * @returns {object} Status configuration object
 */
export const getPermissionStatus = (statusValue) => {
  const statusKey = Object.keys(PermissionStatus).find(
    key => PermissionStatus[key].value === statusValue
  );
  return PermissionStatus[statusKey] || PermissionStatus.PENDING;
};

/**
 * Helper function to get localized status label
 * @param {string} statusValue - Status value
 * @param {string} language - Current language ("ar" or "en")
 * @returns {string} Localized label
 */
export const getLocalizedStatusLabel = (statusValue, language) => {
  const status = getPermissionStatus(statusValue);
  return language === "ar" ? status.labelAr : status.labelEn;
};

/**
 * Expense Categories
 * Common expense categories for asset management
 */
export const ExpenseCategories = [
  {
    value: "maintenance",
    labelEn: "Maintenance",
    labelAr: "صيانة"
  },
  {
    value: "fuel",
    labelEn: "Fuel",
    labelAr: "وقود"
  },
  {
    value: "repair",
    labelEn: "Repair",
    labelAr: "إصلاح"
  },
  {
    value: "parts",
    labelEn: "Parts",
    labelAr: "قطع غيار"
  },
  {
    value: "insurance",
    labelEn: "Insurance",
    labelAr: "تأمين"
  },
  {
    value: "rental",
    labelEn: "Rental",
    labelAr: "إيجار"
  },
  {
    value: "other",
    labelEn: "Other",
    labelAr: "أخرى"
  }
];

/**
 * Helper function to get category label
 * @param {string} categoryValue - Category value
 * @param {string} language - Current language ("ar" or "en")
 * @returns {string} Localized label
 */
export const getCategoryLabel = (categoryValue, language) => {
  const category = ExpenseCategories.find(cat => cat.value === categoryValue);
  if (!category) return categoryValue;
  return language === "ar" ? category.labelAr : category.labelEn;
};
